/**
 * Badge and its `BadgeWith*` variants.
 *
 * Every size, padding, gap and radius here comes from the shared chip tokens in
 * `chip-styles.ts`, which `Pill` is built from too — so a Badge and a Pill of
 * the same size are the same height, type and outline. Colour maps stay local:
 * they are this component's palette, not part of the shared scale.
 */

import { X as CloseX } from '@untitledui/icons';
import type { MouseEventHandler, ReactNode } from 'react';

import { Dot } from '@/lib/foundations/dot-icon';
import { cx } from '@/utils/cx';

import type {
  BadgeColors,
  BadgeTypes,
  BadgeTypeToColorMap,
  FlagTypes,
  IconComponentType,
  Sizes,
} from './badge-types';
import { badgeTypes } from './badge-types';
import {
  CHIP_DISABLED_CLASSES,
  CHIP_ICON_ONLY_CLASSES,
  CHIP_INSET_CLASSES,
  CHIP_INTERACTIVE_CLASSES,
  CHIP_SHAPE_CLASSES,
  CHIP_SIZE_CLASSES,
  type ChipInset,
  chipShapeForBadgeType,
} from './chip-styles';
import { useActivatable } from './useActivatable';

// eslint-disable-next-line react-refresh/only-export-components -- color map constant co-located with Badge component
export const filledColors: Record<
  BadgeColors,
  { root: string; addon: string; addonButton: string }
> = {
  gray: {
    root: 'bg-utility-gray-50 text-utility-gray-700 ring-utility-gray-200',
    addon: 'text-utility-gray-500',
    addonButton:
      'hover:bg-utility-gray-100 text-utility-gray-400 hover:text-utility-gray-500',
  },
  brand: {
    root: 'bg-utility-brand-50 text-utility-brand-700 ring-utility-brand-200',
    addon: 'text-utility-brand-500',
    addonButton:
      'hover:bg-utility-brand-100 text-utility-brand-400 hover:text-utility-brand-500',
  },
  error: {
    root: 'bg-utility-error-50 text-utility-error-700 ring-utility-error-200',
    addon: 'text-utility-error-500',
    addonButton:
      'hover:bg-utility-error-100 text-utility-error-400 hover:text-utility-error-500',
  },
  warning: {
    root: 'bg-utility-warning-50 text-utility-warning-700 ring-utility-warning-200',
    addon: 'text-utility-warning-500',
    addonButton:
      'hover:bg-utility-warning-100 text-utility-warning-400 hover:text-utility-warning-500',
  },
  success: {
    root: 'bg-utility-success-50 text-utility-success-700 ring-utility-success-200',
    addon: 'text-utility-success-500',
    addonButton:
      'hover:bg-utility-success-100 text-utility-success-400 hover:text-utility-success-500',
  },
  'gray-blue': {
    root: 'bg-utility-gray-blue-50 text-utility-gray-blue-700 ring-utility-gray-blue-200',
    addon: 'text-utility-gray-blue-500',
    addonButton:
      'hover:bg-utility-gray-blue-100 text-utility-gray-blue-400 hover:text-utility-gray-blue-500',
  },
  'blue-light': {
    root: 'bg-utility-blue-light-50 text-utility-blue-light-700 ring-utility-blue-light-200',
    addon: 'text-utility-blue-light-500',
    addonButton:
      'hover:bg-utility-blue-light-100 text-utility-blue-light-400 hover:text-utility-blue-light-500',
  },
  blue: {
    root: 'bg-utility-blue-50 text-utility-blue-700 ring-utility-blue-200',
    addon: 'text-utility-blue-500',
    addonButton:
      'hover:bg-utility-blue-100 text-utility-blue-400 hover:text-utility-blue-500',
  },
  indigo: {
    root: 'bg-utility-indigo-50 text-utility-indigo-700 ring-utility-indigo-200',
    addon: 'text-utility-indigo-500',
    addonButton:
      'hover:bg-utility-indigo-100 text-utility-indigo-400 hover:text-utility-indigo-500',
  },
  purple: {
    root: 'bg-utility-purple-50 text-utility-purple-700 ring-utility-purple-200',
    addon: 'text-utility-purple-500',
    addonButton:
      'hover:bg-utility-purple-100 text-utility-purple-400 hover:text-utility-purple-500',
  },
  pink: {
    root: 'bg-utility-pink-50 text-utility-pink-700 ring-utility-pink-200',
    addon: 'text-utility-pink-500',
    addonButton:
      'hover:bg-utility-pink-100 text-utility-pink-400 hover:text-utility-pink-500',
  },
  orange: {
    root: 'bg-utility-orange-50 text-utility-orange-700 ring-utility-orange-200',
    addon: 'text-utility-orange-500',
    addonButton:
      'hover:bg-utility-orange-100 text-utility-orange-400 hover:text-utility-orange-500',
  },
};

const addonOnlyColors = Object.fromEntries(
  Object.entries(filledColors).map(([key, value]) => [
    key,
    { root: '', addon: value.addon },
  ])
) as Record<BadgeColors, { root: string; addon: string }>;

const withPillTypes = {
  [badgeTypes.pillColor]: {
    common: CHIP_SHAPE_CLASSES.pill,
    styles: filledColors,
  },
  [badgeTypes.badgeColor]: {
    common: CHIP_SHAPE_CLASSES.badge,
    styles: filledColors,
  },
  [badgeTypes.badgeModern]: {
    common: `${CHIP_SHAPE_CLASSES.badge} shadow-xs`,
    styles: {
      gray: {
        root: 'bg-primary text-secondary ring-primary',
        addon: 'text-gray-500',
        addonButton:
          'hover:bg-utility-gray-100 text-utility-gray-400 hover:text-utility-gray-500',
      },
    },
  },
};

const withBadgeTypes = {
  [badgeTypes.pillColor]: {
    common: CHIP_SHAPE_CLASSES.pill,
    styles: filledColors,
  },
  [badgeTypes.badgeColor]: {
    common: CHIP_SHAPE_CLASSES.badge,
    styles: filledColors,
  },
  [badgeTypes.badgeModern]: {
    common: `${CHIP_SHAPE_CLASSES.badge} bg-primary text-secondary ring-primary shadow-xs`,
    styles: addonOnlyColors,
  },
};

export type BadgeColor<T extends BadgeTypes> = BadgeTypeToColorMap<
  typeof withPillTypes
>[T];

interface BadgeProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  color?: BadgeColor<T>;
  children: ReactNode;
  className?: string;
  /**
   * Makes the badge activatable. Supply this instead of hand-rolling a pill —
   * a `<button>` styled to look like a badge will not match, because badge
   * geometry (`rounded-full`, `ring-1 ring-inset`, tiny type) is deliberately
   * unlike Button's.
   *
   * Interactivity is applied to the badge's own `<span>` via `role="button"`
   * + `tabIndex` + Enter/Space, NOT a real `<button>`, so an interactive badge
   * stays valid inside flow content (markdown `<p>`, notes, proposal copy) —
   * the same constraint EntityBadge documents. Click is stop-propagated so a
   * badge inside a clickable row doesn't also trigger the row.
   */
  onClick?: () => void;
  /**
   * Accessible name for an interactive badge whose visible text isn't a
   * sufficient label on its own. Ignored when `onClick` is absent.
   */
  'aria-label'?: string;
  /** Blocks activation and dims an interactive badge. */
  isDisabled?: boolean;
  /** Native tooltip — commonly the reason an interactive badge is disabled. */
  title?: string;
}

export const Badge = <T extends BadgeTypes>(props: BadgeProps<T>) => {
  const {
    type = 'pill-color',
    size = 'md',
    color = 'gray',
    children,
    onClick,
    isDisabled = false,
    title,
  } = props;
  const colors = withPillTypes[type];
  const isClickable = Boolean(onClick) && !isDisabled;
  const activatable = useActivatable({ onClick, isDisabled });

  return (
    <span
      className={cx(
        colors.common,
        CHIP_SIZE_CLASSES[size],
        CHIP_INSET_CLASSES[chipShapeForBadgeType(type)][size].text,
        colors.styles[color].root,
        isClickable && CHIP_INTERACTIVE_CLASSES,
        onClick && isDisabled && CHIP_DISABLED_CLASSES,
        props.className
      )}
      // Activation semantics come from the shared useActivatable hook, so Badge
      // and EntityBadge cannot drift. Every attribute it returns is undefined
      // for a presentational badge, so presentational call sites render
      // exactly as they would without it.
      {...activatable}
      aria-label={onClick ? props['aria-label'] : undefined}
      title={title}
    >
      {children}
    </span>
  );
};

interface BadgeWithDotProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  color?: BadgeTypeToColorMap<typeof withBadgeTypes>[T];
  className?: string;
  children: ReactNode;
}

export const BadgeWithDot = <T extends BadgeTypes>(
  props: BadgeWithDotProps<T>
) => {
  const {
    size = 'md',
    color = 'gray',
    type = 'pill-color',
    className,
    children,
  } = props;

  const colors = withBadgeTypes[type];

  return (
    <span
      className={cx(
        colors.common,
        CHIP_SIZE_CLASSES[size],
        CHIP_INSET_CLASSES[chipShapeForBadgeType(type)][size].dot,
        colors.styles[color].root,
        className
      )}
    >
      <Dot className={colors.styles[color].addon} size='sm' />
      {children}
    </span>
  );
};

interface BadgeWithIconProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  color?: BadgeTypeToColorMap<typeof withBadgeTypes>[T];
  iconLeading?: IconComponentType;
  iconTrailing?: IconComponentType;
  children: ReactNode;
  className?: string;
}

export const BadgeWithIcon = <T extends BadgeTypes>(
  props: BadgeWithIconProps<T>
) => {
  const {
    size = 'md',
    color = 'gray',
    type = 'pill-color',
    iconLeading: IconLeading,
    iconTrailing: IconTrailing,
    children,
    className,
  } = props;

  const colors = withBadgeTypes[type];

  // A leading icon wins when both are supplied, matching the render below.
  const inset: ChipInset = IconLeading ? 'iconLeading' : 'iconTrailing';

  return (
    <span
      className={cx(
        colors.common,
        CHIP_SIZE_CLASSES[size],
        CHIP_INSET_CLASSES[chipShapeForBadgeType(type)][size][inset],
        colors.styles[color].root,
        className
      )}
    >
      {IconLeading && (
        <IconLeading
          className={cx(colors.styles[color].addon, 'size-3 stroke-3')}
        />
      )}
      {children}
      {IconTrailing && (
        <IconTrailing
          className={cx(colors.styles[color].addon, 'size-3 stroke-3')}
        />
      )}
    </span>
  );
};

interface BadgeWithFlagProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  flag?: FlagTypes;
  color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
  children: ReactNode;
}

export const BadgeWithFlag = <T extends BadgeTypes>(
  props: BadgeWithFlagProps<T>
) => {
  const {
    size = 'md',
    color = 'gray',
    flag = 'AU',
    type = 'pill-color',
    children,
  } = props;

  const colors = withPillTypes[type];

  return (
    <span
      className={cx(
        colors.common,
        CHIP_SIZE_CLASSES[size],
        CHIP_INSET_CLASSES[chipShapeForBadgeType(type)][size].image,
        colors.styles[color].root
      )}
    >
      <img
        src={`https://www.untitledui.com/images/flags/${flag}.svg`}
        className='size-4 max-w-none rounded-full'
        alt={`${flag} flag`}
      />
      {children}
    </span>
  );
};

interface BadgeWithImageProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  imgSrc: string;
  color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
  children: ReactNode;
}

export const BadgeWithImage = <T extends BadgeTypes>(
  props: BadgeWithImageProps<T>
) => {
  const {
    size = 'md',
    color = 'gray',
    type = 'pill-color',
    imgSrc,
    children,
  } = props;

  const colors = withPillTypes[type];

  return (
    <span
      className={cx(
        colors.common,
        CHIP_SIZE_CLASSES[size],
        CHIP_INSET_CLASSES[chipShapeForBadgeType(type)][size].image,
        colors.styles[color].root
      )}
    >
      <img
        src={imgSrc}
        className='size-4 max-w-none rounded-full'
        alt='Badge image'
      />
      {children}
    </span>
  );
};

interface BadgeWithButtonProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  icon?: IconComponentType;
  color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
  children: ReactNode;
  /**
   * The label for the button.
   */
  buttonLabel?: string;
  /**
   * The click event handler for the button.
   */
  onButtonClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Optional leading content (avatar, icon, etc.) rendered before children.
   */
  leadingContent?: ReactNode;
}

export const BadgeWithButton = <T extends BadgeTypes>(
  props: BadgeWithButtonProps<T>
) => {
  const {
    size = 'md',
    color = 'gray',
    type = 'pill-color',
    icon: Icon = CloseX,
    buttonLabel,
    children,
    leadingContent,
  } = props;

  const colors = withPillTypes[type];

  // Leading content (avatar, icon) reduces the leading padding.
  const inset: ChipInset = leadingContent ? 'buttonWithLeading' : 'button';

  return (
    <span
      className={cx(
        colors.common,
        CHIP_SIZE_CLASSES[size],
        CHIP_INSET_CLASSES[chipShapeForBadgeType(type)][size][inset],
        colors.styles[color].root
      )}
    >
      {leadingContent}
      {children}
      <button
        type='button'
        aria-label={buttonLabel || 'Remove'}
        onClick={props.onButtonClick}
        className={cx(
          'flex cursor-pointer items-center justify-center p-0.5 outline-focus-ring transition duration-100 ease-linear focus-visible:outline-2',
          colors.styles[color].addonButton,
          type === 'pill-color' ? 'rounded-full' : 'rounded-[3px]'
        )}
      >
        <Icon className='size-3 stroke-[3px] transition-inherit-all' />
      </button>
    </span>
  );
};

interface BadgeIconProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  icon: IconComponentType;
  color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
  children?: ReactNode;
}

export const BadgeIcon = <T extends BadgeTypes>(props: BadgeIconProps<T>) => {
  const {
    size = 'md',
    color = 'gray',
    type = 'pill-color',
    icon: Icon,
  } = props;

  const colors = withPillTypes[type];

  return (
    <span
      className={cx(
        colors.common,
        CHIP_ICON_ONLY_CLASSES[chipShapeForBadgeType(type)][size],
        colors.styles[color].root
      )}
    >
      <Icon className={cx('size-3 stroke-[3px]', colors.styles[color].addon)} />
    </span>
  );
};
